import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "../components/ui/Button";
import { format, isBefore, startOfDay } from "date-fns";
import { el } from "date-fns/locale";
import { formatDate, cn } from "../lib/utils";
import {
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  TURNSTILE_SITE_KEY,
} from "../lib/config";
import {
  MiniCalendar,
  MiniCalendarDay,
  MiniCalendarDays,
  MiniCalendarNavigation,
} from "../components/ui/shadcn-io/mini-calendar";
import {
  CalendarIcon,
  Clock3,
  Users,
  User,
  CheckCircle2,
  ArrowLeft,
  ChevronDown,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";

type AvailabilityShift = {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  availableTimes: string[];
};

type AvailabilityResponse = {
  shifts: AvailabilityShift[];
  settings?: {
    maxOnlinePartySize?: number;
  };
};

const DEFAULT_MAX_ONLINE_PARTY_SIZE = 10;

const functionsBase = SUPABASE_URL
  ? `${SUPABASE_URL}/functions/v1/online_reservation`
  : "";

const turnstileSiteKey = TURNSTILE_SITE_KEY;

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId?: string) => void;
      ready?: (callback: () => void) => void;
    };
  }
}

import { APP_NAME } from "../lib/config";

const BRAND = {
  primary: "#326296",
  secondary: "#C9B68D",
  tertiary: "#7DA0C4",
  neutral: "#F8F9FA",
  text: "#1E3550",
  muted: "#5F7388",
};

function SectionTitle({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-2xl border"
          style={{
            backgroundColor: "rgba(125, 160, 196, 0.12)",
            borderColor: "rgba(125, 160, 196, 0.28)",
            color: BRAND.primary,
          }}
        >
          {icon}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ step }: { step: 1 | 2 }) {
  const steps = [
    { id: 1, label: "Επιλογή" },
    { id: 2, label: "Στοιχεία" },
  ] as const;

  return (
    <div className="flex w-full flex-nowrap items-center justify-center md:w-auto md:justify-start">
      {steps.map((item, idx) => {
        const active = step === item.id;
        const completed = step > item.id;

        return (
          <Fragment key={item.id}>
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-transform duration-200",
                  active && "scale-105",
                )}
                style={{
                  backgroundColor: active
                    ? BRAND.primary
                    : completed
                      ? "rgba(125, 160, 196, 0.22)"
                      : "rgba(201, 182, 141, 0.14)",
                  borderColor: active
                    ? BRAND.primary
                    : completed
                      ? "rgba(125, 160, 196, 0.3)"
                      : "rgba(201, 182, 141, 0.28)",
                  color: active ? "#ffffff" : completed ? BRAND.primary : BRAND.muted,
                }}
              >
                {item.id}
              </div>
              <div className="text-center md:text-left">
                <div className="flex flex-wrap items-baseline justify-center gap-x-1.5 md:justify-start">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-slate-400 md:text-xs">
                    Βήμα
                  </span>
                  <span className="text-[10px] font-medium tabular-nums text-slate-500 md:text-xs">
                    {item.id}
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-700">{item.label}</p>
              </div>
            </div>
            {idx === 0 ? (
              <div
                className="mx-1 flex h-10 min-w-[2.5rem] max-w-[4rem] shrink-0 items-center sm:min-w-[3rem] sm:max-w-[5rem] md:mx-2 md:min-w-[3.5rem] md:max-w-[6rem]"
                aria-hidden
              >
                <div className="relative h-px w-full rounded-full bg-slate-200/90">
                  <div
                    className="absolute left-0 top-0 h-px rounded-full transition-all duration-300"
                    style={{
                      width: step > 1 ? "100%" : "50%",
                      backgroundColor: step > 1 ? BRAND.primary : BRAND.tertiary,
                    }}
                  />
                </div>
              </div>
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}

function SummaryPanel({
  selectedDateLabel,
  selectedTime,
  partySize,
  step,
  submitted,
}: {
  selectedDateLabel: string;
  selectedTime: string;
  partySize: number;
  step: 1 | 2;
  submitted: boolean;
}) {
  const summaryItems = [
    { icon: <CalendarIcon className="h-4 w-4" />, label: "Ημερομηνία", value: selectedDateLabel || "Επιλογή ημερομηνίας" },
    { icon: <Clock3 className="h-4 w-4" />, label: "Ώρα", value: selectedTime || "Επιλογή ώρας" },
    { icon: <Users className="h-4 w-4" />, label: "Άτομα", value: `${partySize} άτομα` },
  ];

  return (
    <div
      className="overflow-hidden rounded-[28px] border bg-white/95 shadow-[0_22px_60px_-26px_rgba(50,98,150,0.35)] backdrop-blur"
      style={{ borderColor: "rgba(201, 182, 141, 0.4)" }}
    >
      <div
        className="space-y-3 border-b px-5 py-5"
        style={{
          background: `linear-gradient(135deg, rgba(50,98,150,0.12), rgba(125,160,196,0.22) 55%, rgba(201,182,141,0.22))`,
          borderColor: "rgba(201, 182, 141, 0.35)",
        }}
      >
        <p className="text-xs uppercase tracking-[0.28em]" style={{ color: BRAND.primary }}>
          Κράτηση
        </p>
        <h2 className="text-xl font-semibold text-slate-900">Η σύνοψή σας</h2>
        <p className="text-sm leading-6" style={{ color: BRAND.muted }}>
          {submitted
            ? "Η κράτηση στάλθηκε επιτυχώς. Θα βρείτε παρακάτω τα στοιχεία που καταχωρήθηκαν."
            : step === 1
              ? "Επιλέξτε ημερομηνία, άτομα και ώρα. Η σύνοψη ενημερώνεται αυτόματα."
              : "Ελέγξτε τα στοιχεία σας πριν ολοκληρώσετε την κράτηση."}
        </p>
      </div>

      <div className="space-y-3 px-5 py-5">
        {summaryItems.map((item) => (
          <div
            key={item.label}
            className="flex items-start gap-3 rounded-2xl border px-4 py-3"
            style={{
              backgroundColor: "rgba(248, 249, 250, 0.96)",
              borderColor: "rgba(125, 160, 196, 0.18)",
            }}
          >
            <div
              className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full"
              style={{ backgroundColor: "rgba(50, 98, 150, 0.12)", color: BRAND.primary }}
            >
              {item.icon}
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{item.label}</p>
              <p className="text-sm font-medium text-slate-800">{item.value}</p>
            </div>
          </div>
        ))}

        <div
          className="rounded-2xl border px-4 py-4 text-sm leading-6"
          style={{
            backgroundColor: "rgba(201, 182, 141, 0.12)",
            borderColor: "rgba(201, 182, 141, 0.35)",
            color: BRAND.text,
          }}
        >
          Μετά την αποστολή θα λάβετε επιβεβαίωση στο email σας.
        </div>
      </div>
    </div>
  );
}

export default function ReservationsPreview() {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [partySize, setPartySize] = useState<number>(2);
  const [maxOnlinePartySize, setMaxOnlinePartySize] = useState<number>(
    DEFAULT_MAX_ONLINE_PARTY_SIZE,
  );
  const [availableShifts, setAvailableShifts] = useState<AvailabilityShift[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [showLargePartyHint, setShowLargePartyHint] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Render Turnstile only when step is 2 (contact form). Use explicit rendering per Cloudflare docs:
  // https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
  useEffect(() => {
    if (!turnstileSiteKey || step !== 2) return;

    const EXPLICIT_SCRIPT_URL =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

    const loadScript = () =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          'script[src*="challenges.cloudflare.com/turnstile"]'
        );
        if (existing) {
          if (window.turnstile) return resolve();
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () => reject());
          return;
        }
        const script = document.createElement("script");
        script.src = EXPLICIT_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.head.appendChild(script);
      });

    const renderWidget = async () => {
      try {
        await loadScript();
        const container = captchaRef.current;
        if (!container || !window.turnstile) return;
        if (container.hasChildNodes()) return;

        window.turnstile.render(container, {
          sitekey: turnstileSiteKey,
          size: "flexible",
          callback: (token: string) => setCaptchaToken(token),
          "error-callback": () => setCaptchaToken(""),
          "expired-callback": () => setCaptchaToken(""),
        });
      } catch (err) {
        console.error("[reservations-preview] turnstile load error", err);
      }
    };

    const t = setTimeout(renderWidget, 0);
    return () => clearTimeout(t);
  }, [step]);

  const todayStart = useMemo(() => startOfDay(new Date()), []);
  const isPastDate = useMemo(
    () => !!selectedDate && isBefore(selectedDate, todayStart),
    [selectedDate, todayStart]
  );

  /** Debounce availability GETs so changing party size / date does not hammer the function (429). */
  const AVAILABILITY_DEBOUNCE_MS = 400;

  useEffect(() => {
    if (
      !selectedDate ||
      isPastDate ||
      !functionsBase ||
      !SUPABASE_ANON_KEY ||
      partySize < 1
    ) {
      if (isPastDate) {
        setAvailableShifts([]);
        setSelectedTime("");
      }
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void (async () => {
        setLoadingAvailability(true);
        setMessage(null);
        try {
          const params = new URLSearchParams({
            date: formatDate(selectedDate),
            partySize: String(partySize),
          });
          const res = await fetch(`${functionsBase}/availability?${params.toString()}`, {
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
            signal: controller.signal,
          });
          if (!res.ok) {
            let detail = "";
            try {
              const body = (await res.clone().json()) as {
                error?: string;
                settings?: { maxOnlinePartySize?: number };
              };
              detail = body?.error || "";
              const nextMax = body?.settings?.maxOnlinePartySize;
              if (typeof nextMax === "number" && nextMax >= 1) {
                setMaxOnlinePartySize(nextMax);
                setPartySize((current) => Math.min(current, nextMax));
              }
            } catch {
              detail = (await res.text().catch(() => "")) || "";
            }
            const err = new Error(
              `[reservations-preview] availability HTTP ${res.status}${detail ? `: ${detail}` : ""}`,
            );
            (err as Error & { status?: number }).status = res.status;
            throw err;
          }
          const json = (await res.json()) as AvailabilityResponse;
          const nextMax = Math.max(
            1,
            json.settings?.maxOnlinePartySize ?? DEFAULT_MAX_ONLINE_PARTY_SIZE,
          );
          setMaxOnlinePartySize(nextMax);
          setPartySize((current) => Math.min(current, nextMax));
          setAvailableShifts(json.shifts || []);
          const stillValid = json.shifts.some((s) =>
            s.availableTimes.includes(selectedTime),
          );
          if (!stillValid) {
            setSelectedTime("");
          }
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") {
            return;
          }
          const status =
            err instanceof Error && "status" in err
              ? (err as Error & { status?: number }).status
              : undefined;
          console.error("[reservations-preview] availability error", err);
          if (status === 401) {
            console.warn(
              "[reservations-preview] availability unauthorized (401). Check function JWT setting and public key configuration.",
            );
          } else if (status === 429) {
            console.warn("[reservations-preview] availability rate limited (429).");
          }
          setAvailableShifts([]);
          setSelectedTime("");
        } finally {
          setLoadingAvailability(false);
        }
      })();
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [selectedDate, partySize, isPastDate]);

  const partySizeOptions = useMemo(
    () => Array.from({ length: maxOnlinePartySize }, (_, idx) => idx + 1),
    [maxOnlinePartySize],
  );

  const allTimes = useMemo(() => {
    if (isPastDate) {
      return [];
    }
    // helper to turn "HH:MM" into minutes
    const toMinutes = (t: string) => {
      const [h, m] = t.split(":").map((n) => parseInt(n, 10));
      return h * 60 + m;
    };

    const times = new Set<string>();
    const today = new Date();
    const isToday =
      selectedDate &&
      today.toDateString() === (selectedDate as Date).toDateString();
    const nowMinutes = today.getHours() * 60 + today.getMinutes();

    availableShifts.forEach((s) =>
      s.availableTimes.forEach((t) => {
        // If viewing today, hide past times (<= current time)
        if (isToday && toMinutes(t) <= nowMinutes) return;
        times.add(t);
      })
    );

    return Array.from(times).sort();
  }, [availableShifts, selectedDate, isPastDate]);

  const requiresCaptcha = !!turnstileSiteKey;
  const canSubmit =
    !!selectedDate &&
    !isPastDate &&
    !!selectedTime &&
    !!name.trim() &&
    !!phone.trim() &&
    !!email.trim() &&
    partySize > 0 &&
    !!functionsBase &&
    !!SUPABASE_ANON_KEY &&
    (!requiresCaptcha || !!captchaToken);

  const canContinueStep1 =
    !!selectedDate &&
    !isPastDate &&
    !!selectedTime &&
    partySize > 0 &&
    !!functionsBase &&
    !!SUPABASE_ANON_KEY;

  const selectedDateLabel = selectedDate
    ? format(selectedDate as Date, "PPP", { locale: el })
    : "";

  const resetForm = () => {
    setSubmitted(false);
    setMessage(null);
    setError(null);
    setSelectedTime("");
    setPartySize(Math.min(2, maxOnlinePartySize));
    setName("");
    setPhone("");
    setEmail("");
    setNotes("");
    setShowLargePartyHint(false);
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !selectedDate) return;
    
    return Sentry.startSpan(
      {
        op: "reservation.submit",
        name: "Submit Reservation",
      },
      async (span) => {
        setSubmitting(true);
        setError(null);
        setMessage(null);
        try {
          span?.setAttribute("partySize", partySize);
          span?.setAttribute("date", formatDate(selectedDate));
          span?.setAttribute("time", selectedTime);

          const res = await fetch(functionsBase, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              date: selectedDate ? formatDate(selectedDate) : "",
              time: selectedTime,
              partySize,
              name: name.trim(),
              phone: phone.trim(),
              email: email.trim(),
              notes: notes.trim() || undefined,
              captchaToken: requiresCaptcha ? captchaToken : undefined,
            }),
          });

          const json = await res.json().catch(() => ({ error: null }));

          if (!res.ok) {
            // Show specific error message from backend if available
            const errorMsg = json.error || "Η κράτηση δεν ολοκληρώθηκε. Προσπαθήστε ξανά ή καλέστε το κατάστημα.";
            throw new Error(errorMsg);
          }

          setMessage("Η κράτηση καταχωρήθηκε με επιτυχία.");
          setSubmitted(true);
          span?.setStatus({ code: 1, message: "ok" }); // 1 = OK
        } catch (err) {
          console.error("[reservations-preview] submit error", err);
          // Show the error message from the backend or a default one
          const errorMsg = err instanceof Error ? err.message : "Η κράτηση δεν ολοκληρώθηκε. Προσπαθήστε ξανά ή καλέστε το κατάστημα.";
          setError(errorMsg);
          span?.setStatus({ code: 2, message: "internal_error" }); // 2 = ERROR
          Sentry.captureException(err);
        } finally {
          setSubmitting(false);
        }
      }
    );
  };

  return (
    <div
      className="min-h-screen px-4 py-6 md:px-6 md:py-8 lg:px-8"
      style={{
        background: `radial-gradient(circle at top left, rgba(125,160,196,0.26), transparent 28%), radial-gradient(circle at top right, rgba(201,182,141,0.2), transparent 22%), ${BRAND.neutral}`,
      }}
    >
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <section
          className="relative overflow-hidden rounded-[32px] border px-6 py-6 shadow-[0_30px_70px_-30px_rgba(50,98,150,0.35)] md:px-8 md:py-8"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,249,250,0.96) 52%, rgba(244,238,226,0.94))",
            borderColor: "rgba(201, 182, 141, 0.42)",
          }}
        >
          <div
            aria-hidden
            className="absolute -right-10 -top-10 h-36 w-36 rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(125, 160, 196, 0.22)" }}
          />
          <div
            aria-hidden
            className="absolute bottom-0 left-0 h-28 w-28 rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(201, 182, 141, 0.18)" }}
          />

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
            <div className="space-y-4 text-center lg:text-left">
              <p
                className="text-xs uppercase tracking-[0.34em]"
                style={{ color: BRAND.primary }}
              >
                Κράτηση τραπεζιού
              </p>
              <div className="space-y-3">
                <h1
                  className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl"
                  style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                >
                  Κλείστε το τραπέζι σας στο Εν Πλω
                </h1>
                <p className="mx-auto max-w-2xl text-sm leading-7 md:text-base lg:mx-0" style={{ color: BRAND.muted }}>
                  Κλείστε εύκολα το τραπέζι σας στο εστιατόριό μας. Επιλέξτε ημερομηνία, ώρα και
                  αριθμό ατόμων και στείλτε την κράτησή σας σε λίγα δευτερόλεπτα.
                </p>
              </div>
            </div>

            <div
              className="mx-auto flex w-full max-w-[min(100%,320px)] items-center justify-center overflow-hidden rounded-[28px] px-4 py-4 sm:px-6 sm:py-5"
              style={{
                background:
                  "linear-gradient(180deg, rgba(125,160,196,0.16), rgba(255,255,255,0.92) 55%, rgba(201,182,141,0.14))",
              }}
            >
              <img
                src="/ΕΝ_ΠΛΩ_ΛΟΓΟΤΥΠΟ_2-01.png"
                alt={`${APP_NAME} logo`}
                className="h-auto max-h-[100px] w-auto max-w-full object-contain sm:max-h-[120px]"
              />
            </div>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <div
            className="overflow-hidden rounded-[32px] border bg-white/95 shadow-[0_26px_70px_-28px_rgba(50,98,150,0.34)]"
            style={{ borderColor: "rgba(201, 182, 141, 0.4)" }}
          >
            <div
              className="space-y-5 border-b px-6 py-6 md:px-8"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,249,250,0.96) 52%, rgba(244,238,226,0.9))",
                borderColor: "rgba(201, 182, 141, 0.32)",
              }}
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Στοιχεία κράτησης</p>
                  <h2
                    className="text-2xl font-semibold text-slate-900"
                    style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                  >
                    Κράτηση τραπεζιού
                  </h2>
                  <p className="max-w-xl text-sm leading-6" style={{ color: BRAND.muted }}>
                    Συμπληρώστε τα στοιχεία της κράτησής σας και στείλτε το αίτημά σας με ασφάλεια.
                  </p>
                </div>
                <StepIndicator step={step} />
              </div>
            </div>

            <div className="px-6 py-6 md:px-8 md:py-8">
              {submitted ? (
                <div className="space-y-6 text-center">
                  <div
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
                    style={{ backgroundColor: "rgba(50, 98, 150, 0.12)", color: BRAND.primary }}
                  >
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <div className="space-y-2">
                    <h2
                      className="text-2xl font-semibold text-slate-900"
                      style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                    >
                      Η κράτηση καταχωρήθηκε
                    </h2>
                    <p className="text-sm leading-6" style={{ color: BRAND.muted }}>
                      Η αίτησή σας στάλθηκε επιτυχώς με τα παρακάτω στοιχεία.
                    </p>
                  </div>

                  <div className="mx-auto grid max-w-xl gap-3 sm:grid-cols-3">
                    {[
                      { icon: <CalendarIcon className="h-4 w-4" />, value: selectedDateLabel || "—" },
                      { icon: <Clock3 className="h-4 w-4" />, value: selectedTime || "—" },
                      { icon: <Users className="h-4 w-4" />, value: `${partySize} άτομα` },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border px-4 py-4 text-sm"
                        style={{
                          backgroundColor: "rgba(248, 249, 250, 0.96)",
                          borderColor: "rgba(125, 160, 196, 0.2)",
                        }}
                      >
                        <div
                          className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full"
                          style={{ backgroundColor: "rgba(50, 98, 150, 0.12)", color: BRAND.primary }}
                        >
                          {item.icon}
                        </div>
                        <p className="font-medium text-slate-800">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div
                    className="rounded-2xl border px-4 py-4 text-sm leading-6 lg:hidden"
                    style={{
                      backgroundColor: "rgba(201, 182, 141, 0.12)",
                      borderColor: "rgba(201, 182, 141, 0.35)",
                      color: BRAND.text,
                    }}
                  >
                    Μετά την αποστολή θα λάβετε επιβεβαίωση στο email σας.
                  </div>

                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      className="rounded-2xl border px-6 py-3 text-sm font-medium"
                      style={{
                        borderColor: "rgba(50, 98, 150, 0.22)",
                        color: BRAND.primary,
                        backgroundColor: "#ffffff",
                      }}
                      onClick={resetForm}
                    >
                      Νέα κράτηση
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {step === 1 ? (
                    <div className="space-y-6">
                      <div
                        className="rounded-[28px] border p-5 md:p-6"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(248,249,250,0.98), rgba(255,255,255,0.96))",
                          borderColor: "rgba(125, 160, 196, 0.2)",
                        }}
                      >
                        <SectionTitle
                          icon={<CalendarIcon className="h-5 w-5" />}
                          title="Επιλέξτε ημερομηνία"
                          subtitle="Διαλέξτε την ημέρα που σας εξυπηρετεί."
                        />
                        <div className="mt-5">
                          <MiniCalendar
                            value={selectedDate}
                            onValueChange={setSelectedDate}
                            className="relative w-full border-0 bg-transparent p-0 shadow-none"
                            days={7}
                            minDate={new Date()}
                          >
                            <MiniCalendarNavigation
                              direction="prev"
                              className="absolute -left-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border bg-white p-0 shadow-sm transition-colors hover:bg-slate-50"
                              style={{ borderColor: "rgba(125, 160, 196, 0.22)" }}
                            >
                              <ChevronLeftIcon className="h-4 w-4" style={{ color: BRAND.muted }} />
                            </MiniCalendarNavigation>
                            <MiniCalendarDays className="mx-10 w-full overflow-x-auto">
                              {(date) => (
                                <MiniCalendarDay
                                  date={date}
                                  key={date.toISOString()}
                                  className="min-w-[3.25rem] flex-1"
                                />
                              )}
                            </MiniCalendarDays>
                            <MiniCalendarNavigation
                              direction="next"
                              className="absolute -right-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border bg-white p-0 shadow-sm transition-colors hover:bg-slate-50"
                              style={{ borderColor: "rgba(125, 160, 196, 0.22)" }}
                            >
                              <ChevronRightIcon className="h-4 w-4" style={{ color: BRAND.muted }} />
                            </MiniCalendarNavigation>
                          </MiniCalendar>
                        </div>
                      </div>

                      <div className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                        <div
                          className="rounded-[28px] border p-5 md:p-6"
                          style={{
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,249,250,0.96))",
                            borderColor: "rgba(201, 182, 141, 0.35)",
                          }}
                        >
                          <SectionTitle
                            icon={<Users className="h-5 w-5" />}
                            title="Αριθμός ατόμων"
                            subtitle="Επιλέξτε τη σύνθεση της παρέας σας."
                          />
                          <div className="mt-5 grid grid-cols-4 gap-3">
                            {partySizeOptions.map((num) => {
                              const active = partySize === num;
                              return (
                                <button
                                  key={num}
                                  onClick={() => setPartySize(num)}
                                  className={cn(
                                    "cursor-pointer rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-200",
                                    active && "translate-y-[-1px] shadow-md",
                                  )}
                                  style={{
                                    backgroundColor: active ? BRAND.primary : "#ffffff",
                                    borderColor: active ? BRAND.primary : "rgba(125, 160, 196, 0.2)",
                                    color: active ? "#ffffff" : BRAND.text,
                                  }}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-4 flex justify-center">
                            <button
                              type="button"
                              onClick={() => setShowLargePartyHint((current) => !current)}
                              className="inline-flex cursor-pointer flex-col items-center gap-1 rounded-2xl px-4 py-2 text-center transition-colors hover:bg-slate-50"
                              style={{ color: BRAND.muted }}
                            >
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 transition-transform duration-200",
                                  showLargePartyHint && "rotate-180",
                                )}
                              />
                              <span className="text-xs font-medium">
                                Χρειάζεστε μεγαλύτερη παρέα;
                              </span>
                            </button>
                          </div>
                          {showLargePartyHint ? (
                            <div
                              className="mt-3 rounded-2xl border px-4 py-4 text-sm leading-6"
                              style={{
                                backgroundColor: "rgba(248, 249, 250, 0.96)",
                                borderColor: "rgba(201, 182, 141, 0.35)",
                                color: BRAND.text,
                              }}
                            >
                              Οι online επιλογές εμφανίζονται έως {maxOnlinePartySize} άτομα.
                              Αν η παρέα σας είναι μεγαλύτερη, επιλέξτε {maxOnlinePartySize} και
                              στο επόμενο βήμα γράψτε στις σημειώσεις πόσα άτομα θα είστε συνολικά.
                            </div>
                          ) : null}
                        </div>

                        <div
                          className="rounded-[28px] border p-5 md:p-6"
                          style={{
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,249,250,0.96))",
                            borderColor: "rgba(125, 160, 196, 0.2)",
                          }}
                        >
                          <SectionTitle
                            icon={<Clock3 className="h-5 w-5" />}
                            title="Επιλέξτε ώρα"
                            subtitle="Οι διαθέσιμες ώρες εμφανίζονται δυναμικά."
                          />
                          <div className="mt-5 space-y-4">
                            {loadingAvailability ? (
                              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                {Array.from({ length: 6 }).map((_, idx) => (
                                  <div
                                    key={idx}
                                    className="h-11 rounded-2xl animate-pulse"
                                    style={{ backgroundColor: "rgba(125, 160, 196, 0.16)" }}
                                  />
                                ))}
                              </div>
                            ) : allTimes.length === 0 ? (
                              <div
                                className="rounded-2xl border px-4 py-4 text-sm leading-6"
                                style={{
                                  backgroundColor: "rgba(248, 249, 250, 0.96)",
                                  borderColor: "rgba(201, 182, 141, 0.35)",
                                  color: BRAND.muted,
                                }}
                              >
                                Δεν βρέθηκε διαθέσιμη ώρα για την επιλογή σας. Δοκιμάστε άλλη
                                ημερομηνία ή ώρα.
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                {allTimes.map((time) => {
                                  const active = selectedTime === time;
                                  return (
                                    <button
                                      key={time}
                                      onClick={() => setSelectedTime(time)}
                                      className={cn(
                                        "cursor-pointer rounded-2xl border px-4 py-3 text-sm font-medium transition-all duration-200",
                                        active && "translate-y-[-1px] shadow-md",
                                      )}
                                      style={{
                                        backgroundColor: active ? "rgba(50, 98, 150, 0.08)" : "#ffffff",
                                        borderColor: active ? BRAND.primary : "rgba(125, 160, 196, 0.2)",
                                        color: active ? BRAND.primary : BRAND.text,
                                      }}
                                      disabled={submitting}
                                    >
                                      {time}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          className="w-full rounded-2xl px-8 py-3 text-white sm:w-auto"
                          style={{ backgroundColor: BRAND.primary }}
                          disabled={!canContinueStep1 || submitting}
                          onClick={() => setStep(2)}
                        >
                          Συνέχεια
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between gap-4">
                        <button
                          onClick={() => setStep(1)}
                          className="flex cursor-pointer items-center gap-2 text-sm font-medium transition-colors"
                          style={{ color: BRAND.muted }}
                        >
                          <ArrowLeft className="h-4 w-4" />
                          Πίσω στην επιλογή
                        </button>
                        <p className="text-sm text-slate-400">Στοιχεία επικοινωνίας</p>
                      </div>

                      <div
                        className="rounded-[28px] border p-5 md:p-6"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,249,250,0.96))",
                          borderColor: "rgba(125, 160, 196, 0.2)",
                        }}
                      >
                        <SectionTitle
                          icon={<User className="h-5 w-5" />}
                          title="Στοιχεία επικοινωνίας"
                          subtitle="Συμπληρώστε τα στοιχεία σας για να ολοκληρωθεί η κράτηση."
                        />

                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                          <div className="space-y-2 md:col-span-2">
                            <label htmlFor="booking-name" className="text-sm font-medium text-slate-800">
                              Όνομα
                            </label>
                            <input
                              id="booking-name"
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              className="allow-select w-full rounded-2xl border bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-shadow focus-visible:ring-2"
                              style={{
                                borderColor: "rgba(125, 160, 196, 0.24)",
                                boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
                              }}
                              placeholder="Πλήρες όνομα"
                              disabled={submitting}
                            />
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="booking-email" className="text-sm font-medium text-slate-800">
                              Email
                            </label>
                            <input
                              id="booking-email"
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="allow-select w-full rounded-2xl border bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-shadow focus-visible:ring-2"
                              style={{
                                borderColor: "rgba(125, 160, 196, 0.24)",
                                boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
                              }}
                              placeholder="email@example.com"
                              disabled={submitting}
                            />
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="booking-phone" className="text-sm font-medium text-slate-800">
                              Κινητό (υποχρεωτικό)
                            </label>
                            <input
                              id="booking-phone"
                              type="tel"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={phone}
                              onKeyDown={(e) => {
                                const allowedKeys = [
                                  "Backspace",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Tab",
                                  "Home",
                                  "End",
                                  "Enter",
                                ];
                                if (allowedKeys.includes(e.key)) return;
                                if (/^[0-9]$/.test(e.key)) return;
                                e.preventDefault();
                              }}
                              onChange={(e) => {
                                const digitsOnly = e.target.value.replace(/\D/g, "");
                                setPhone(digitsOnly);
                              }}
                              className="allow-select w-full rounded-2xl border bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-shadow focus-visible:ring-2"
                              style={{
                                borderColor: "rgba(125, 160, 196, 0.24)",
                                boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
                              }}
                              placeholder="69xxxxxxxx"
                              disabled={submitting}
                            />
                          </div>

                          <div className="space-y-2 md:col-span-2">
                            <label htmlFor="booking-notes" className="text-sm font-medium text-slate-800">
                              Σημειώσεις
                            </label>
                            <textarea
                              id="booking-notes"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              rows={4}
                              className="allow-select w-full rounded-2xl border bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-shadow focus-visible:ring-2"
                              style={{
                                borderColor: "rgba(125, 160, 196, 0.24)",
                                boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
                              }}
                              placeholder={`Για μεγαλύτερη παρέα από ${maxOnlinePartySize} άτομα ή για οτιδήποτε άλλο θέλετε να γνωρίζουμε.`}
                              disabled={submitting}
                            />
                            <p className="text-xs leading-5" style={{ color: BRAND.muted }}>
                              Για μεγαλύτερες παρέες από {maxOnlinePartySize} άτομα, γράψτε εδώ το
                              πραγματικό πλήθος ατόμων και όποια άλλη χρήσιμη πληροφορία.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        className="lg:hidden rounded-[28px] border p-5 md:p-6"
                        style={{
                          backgroundColor: "rgba(248, 249, 250, 0.96)",
                          borderColor: "rgba(201, 182, 141, 0.35)",
                        }}
                      >
                        <SectionTitle
                          icon={<CalendarIcon className="h-5 w-5" />}
                          title="Γρήγορη σύνοψη"
                          subtitle="Μια τελευταία ματιά πριν την αποστολή."
                        />
                        <div className="mt-5 flex flex-wrap gap-3 text-sm text-slate-700">
                          <span
                            className="flex items-center gap-2 rounded-full border px-4 py-2"
                            style={{
                              backgroundColor: "#ffffff",
                              borderColor: "rgba(125, 160, 196, 0.2)",
                            }}
                          >
                            <CalendarIcon className="h-4 w-4" style={{ color: BRAND.primary }} />
                            {selectedDateLabel || "—"}
                          </span>
                          <span
                            className="flex items-center gap-2 rounded-full border px-4 py-2"
                            style={{
                              backgroundColor: "#ffffff",
                              borderColor: "rgba(125, 160, 196, 0.2)",
                            }}
                          >
                            <Clock3 className="h-4 w-4" style={{ color: BRAND.primary }} />
                            {selectedTime || "—"}
                          </span>
                          <span
                            className="flex items-center gap-2 rounded-full border px-4 py-2"
                            style={{
                              backgroundColor: "#ffffff",
                              borderColor: "rgba(125, 160, 196, 0.2)",
                            }}
                          >
                            <Users className="h-4 w-4" style={{ color: BRAND.primary }} />
                            {partySize} άτομα
                          </span>
                        </div>
                      </div>

                      {requiresCaptcha ? (
                        <div
                          className="rounded-[28px] border p-5 md:p-6"
                          style={{
                            background:
                              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,249,250,0.96))",
                            borderColor: "rgba(125, 160, 196, 0.2)",
                          }}
                        >
                          <p className="text-sm font-medium text-slate-900">Επαλήθευση</p>
                          <div ref={captchaRef} className="mt-4 min-h-[65px]" aria-label="turnstile captcha" />
                        </div>
                      ) : null}

                      {error ? (
                        <div
                          className="rounded-2xl border px-4 py-3 text-sm"
                          style={{
                            backgroundColor: "rgba(254, 242, 242, 0.95)",
                            borderColor: "rgba(248, 113, 113, 0.28)",
                            color: "#b91c1c",
                          }}
                        >
                          {error}
                        </div>
                      ) : null}
                      {message && !submitted ? (
                        <div
                          className="rounded-2xl border px-4 py-3 text-sm"
                          style={{
                            backgroundColor: "rgba(240, 253, 244, 0.95)",
                            borderColor: "rgba(74, 222, 128, 0.24)",
                            color: "#166534",
                          }}
                        >
                          {message}
                        </div>
                      ) : null}

                      <Button
                        className="w-full rounded-2xl px-8 py-3 text-white"
                        style={{ backgroundColor: BRAND.primary }}
                        disabled={!canSubmit || submitting}
                        onClick={handleSubmit}
                      >
                        {submitting ? "Αποστολή..." : "Αποστολή κράτησης"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <aside className="hidden lg:block lg:sticky lg:top-6">
            <SummaryPanel
              selectedDateLabel={selectedDateLabel}
              selectedTime={selectedTime}
              partySize={partySize}
              step={step}
              submitted={submitted}
            />
          </aside>
        </div>
      </div>
    </div>
  );
}
